import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Fragment, useState, useMemo, useEffect, useRef } from 'react';
import { formatDateDisplay } from '../utils/date';
import { formatPaymentMethod, BankAccountInfo } from '../utils/billingDisplay';
import { useLabelParts, useLabels, useStatusLabel } from '../config/useLabels';
import { Sparkles } from 'lucide-react';
import { useUrlTabState } from '../hooks/useUrlTabState';
import { getErrorMessage } from '../utils/errorMessage';
import { BrandBadge } from '../components/BrandBadge';
import { useCartHeldInvoiceIds } from '../hooks/useCartHeldInvoiceIds';
import { useLiveZelleCarts } from '../hooks/useLiveZelleCarts';
import { balanceDueCents, compareDueNow, isDueNow, isPaymentInFlight } from '../utils/cart';
import { checkoutPath } from '../utils/checkoutSource';
import { trackEvent, getSessionId } from '../utils/analytics';

type PaymentsTab = 'invoices' | 'history';
const PAYMENTS_TABS: readonly PaymentsTab[] = ['invoices', 'history'];

interface Invoice {
  id: string;
  total_amount: number;
  amount_paid: number;
  due_date: string;
  period_start: string;
  period_end: string;
  status: string;
  is_prorated?: boolean;
  enrollment: {
    id: string;
    metadata?: { level?: string };
    student: {
      first_name: string;
      last_name: string;
    };
    program: {
      name: string;
      billing_type: string;
      price: number;
      brand_key?: string | null;
    };
  };
  payments?: Payment[];
}

interface Payment {
  id: string;
  amount: number;
  payment_method: string;
  payment_date: string;
  status: string;
  invoice_id: string;
  paid_by?: string;
  created_at?: string;
  bank_account_setup_intents?: BankAccountInfo;
  invoice: {
    id: string;
    total_amount: number;
    amount_paid: number;
    due_date: string;
    period_start: string;
    period_end: string;
    status: string;
    enrollment: {
      id: string;
      metadata?: { level?: string };
      student: {
        first_name: string;
        last_name: string;
      };
      program: {
        name: string;
        brand_key?: string | null;
      };
    };
  };
  refunds: {
    id: string;
    amount: number;
    status: string;
    refund_date: string;
    refund_method?: string;
    manual_refund_notes?: string;
  }[];
}

interface Student {
  id: string;
  first_name: string;
  last_name: string;
}

interface Program {
  id: string;
  name: string;
  type: string;
  price: number;
  billing_type: string;
  brand_key?: string | null;
  active: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

interface Refund {
  id: string;
  amount: number;
  status: string;
  refund_date: string;
}

type SupabasePaymentResponse = {
  id: string;
  amount: number;
  payment_method: string;
  payment_date: string;
  status: string;
  invoice_id: string;
  created_at: string;
  bank_account_setup_intents?: BankAccountInfo;
  invoice: {
    id: string;
    total_amount: number;
    amount_paid: number;
    due_date: string;
    period_start: string;
    period_end: string;
    status: string;
    enrollment: {
      id: string;
      student: Student;
      program: Program;
    };
  };
  refunds: Refund[];
};

type BundleShareRow = {
  invoice_id: string;
  snapshot_balance_cents: number;
  payment_carts: { id: string; parent_id: string; created_at: string };
  invoice: SupabasePaymentResponse['invoice'];
};

/**
 * Each invoice's share of a bank bundle payment whose ACH debit is still clearing, as a payment
 * history entry. A bundle writes its payments rows only when the money lands, 3-5 business days
 * later; until then this is what lists it, as "processing", the way a single ACH payment's own
 * row does. Once it lands the cart is settled, so its real rows replace these.
 */
export async function fetchBundleSharesInFlight(): Promise<Payment[]> {
  const { data, error } = await supabase
    .from('payment_cart_items')
    .select(`
      invoice_id,
      snapshot_balance_cents,
      payment_carts!inner ( id, parent_id, created_at, status, charge_status ),
      invoice:invoices!inner (
        id,
        total_amount,
        amount_paid,
        due_date,
        period_start,
        period_end,
        status,
        enrollment:enrollments!inner (
          id,
          metadata,
          student:students ( id, first_name, last_name ),
          program:programs ( name, price, billing_type, brand_key )
        )
      )
    `)
    .in('payment_carts.status', ['open', 'partially_funded'])
    .eq('payment_carts.charge_status', 'processing');
  // Best-effort, like the rest of the in-flight lookups: a failed read hides nothing that is paid.
  if (error) return [];
  return ((data ?? []) as unknown as BundleShareRow[]).map((row) => ({
    id: `bundle-${row.payment_carts.id}-${row.invoice_id}`,
    amount: row.snapshot_balance_cents / 100,
    payment_method: 'ach',
    payment_date: row.payment_carts.created_at,
    status: 'processing',
    invoice_id: row.invoice_id,
    paid_by: row.payment_carts.parent_id,
    created_at: row.payment_carts.created_at,
    invoice: row.invoice,
    refunds: [],
  }));
}

// Define a type for the raw Supabase response for invoices

type RawEnrollment = {
  id: string;
  metadata?: { level?: string };
  student?: { first_name: string; last_name: string }[];
  program?: {
    id: string;
    name: string;
    type: string;
    price: number;
    billing_type: string;
    brand_key?: string | null;
    active: boolean;
    created_at?: string | null;
    updated_at?: string | null;
  }[];
};

// Define a type for the raw Supabase response for payments

type RawPayment = {
  id: string;
  amount: number;
  payment_method: string;
  payment_date: string;
  status: string;
  bank_account_setup_intents?: BankAccountInfo;
};

type RawInvoice = {
  id: string;
  enrollment_id: string;
  total_amount: number;
  amount_paid: number;
  due_date: string;
  period_start: string;
  period_end: string;
  status: string;
  created_at: string;
  enrollment?: RawEnrollment[];
  payments?: RawPayment[];
};

type StudentParent = { student_id: string };

function getStatusColor(status: string) {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'refunded':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

function getInvoiceStatusColor(status: string) {
  switch (status.toLowerCase()) {
    case 'paid': return 'bg-green-100 text-green-800';
    case 'partial': return 'bg-blue-100 text-blue-800';
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'overdue': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

export function Payments() {
  const explainInvoice = useLabelParts()('genie.chip.explainInvoices');
  const { user } = useAuth();
  const t = useLabels();
  const statusLabel = useStatusLabel();
  const location = useLocation();
  const [activeTab, setActiveTab] = useUrlTabState<PaymentsTab>('tab', PAYMENTS_TABS, 'invoices');
  // Track which tabs have been visited to enable lazy query loading. Seeded from
  // the tab the URL selected so a shared `?tab=history` link fetches on the first
  // render instead of showing an empty tab until it is clicked.
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(() => new Set([activeTab]));
  // Covers tab changes that don't come from the tab buttons (a hand-edited URL,
  // a history navigation) — the click handlers alone would miss those.
  useEffect(() => {
    setVisitedTabs(prev => (prev.has(activeTab) ? prev : new Set([...prev, activeTab])));
  }, [activeTab]);
  const [range, setRange] = useState<'month' | 'year' | 'all'>('year');
  // null = "all students selected" (default). Using null avoids the useEffect
  // race where [] briefly shows "no students" before studentOptions loads.
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[] | null>(null);
  const navigate = useNavigate();

  const { data: parentData, error: parentError } = useQuery({
    queryKey: ['current-parent', user?.email],
    queryFn: async () => {
      if (!user?.email) throw new Error('No authenticated user');
      const { data, error } = await supabase
        .from('parents')
        .select('id')
        .eq('email', user.email)
        .single();
      if (error && error.code === 'PGRST116') throw new Error('Parent not found');
      if (error) throw error;
      return data;
    },
    enabled: !!user?.email,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: (failureCount, error) => {
      if ((error as Error).message === 'Parent not found') return false;
      return failureCount < 3;
    }
  });

  // Handle parent not found error with useEffect
  useEffect(() => {
    if (parentError && parentError.message === 'Parent not found') {
      navigate('/dashboard', { replace: true });
    }
  }, [parentError, navigate]);

  // BHCS Cash balance for tab label
  const { data: cashAccountId } = useQuery<string | null>({
    queryKey: ['payments-cash-account', parentData?.id],
    queryFn: async () => {
      if (!parentData?.id) return null;
      const { data, error } = await supabase
        .from('balance_accounts')
        .select('id')
        .eq('parent_id', parentData.id)
        .maybeSingle();
      if (error) throw error;
      return data?.id ?? null;
    },
    enabled: !!parentData?.id,
  });

  const { data: cashTotalCents } = useQuery<number>({
    queryKey: ['payments-cash-balance', cashAccountId],
    queryFn: async () => {
      if (!cashAccountId) return 0;
      const { data, error } = await supabase.rpc('get_balance', { p_account_id: cashAccountId });
      if (error) throw error;
      return data?.[0]?.total_cents ?? 0;
    },
    enabled: !!cashAccountId,
  });

  // Fetch students for filter dropdown
  // Define proper interfaces for Supabase response
  interface StudentData {
    id: string;
    first_name: string;
    active: boolean;
  }

  interface StudentParentRow {
    student: StudentData | StudentData[];
  }

  const { data: studentOptions, isLoading: isStudentsLoading } = useQuery<StudentData[] | null>({
    queryKey: ['students-for-parent', parentData?.id],
    queryFn: async () => {
      if (!parentData?.id) return [];
      const { data, error } = await supabase
        .from('student_parents')
        .select('student:students ( id, first_name, active )')
        .eq('parent_id', parentData.id)
        .eq('can_pay', true);
      if (error) {
        console.error('Error fetching students:', error);
        throw error;
      }
      const list = (data as StudentParentRow[] | null) ?? [];
      return list.map((row: StudentParentRow) => {
        const student = row.student;
        // Handle both array and direct object formats
        return Array.isArray(student) ? student[0] : student;
      }).filter(Boolean) as StudentData[];
    },
    enabled: !!parentData?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const toggleStudent = (id: string) => {
    const all = (studentOptions ?? []).map(s => s.id);
    setSelectedStudentIds(prev => {
      const current = prev ?? all;
      return current.includes(id) ? current.filter(x => x !== id) : [...current, id];
    });
  };

  // Compute date range bounds for filters
  const { startIso, endIso } = useMemo(() => {
    if (range === 'all') return { startIso: null as string | null, endIso: null as string | null };
    const now = new Date();
    if (range === 'month') {
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0));
      const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0));
      return { startIso: start.toISOString(), endIso: end.toISOString() };
    }
    // year
    const start = new Date(Date.UTC(now.getUTCFullYear(), 0, 1, 0, 0, 0));
    const end = new Date(Date.UTC(now.getUTCFullYear() + 1, 0, 1, 0, 0, 0));
    return { startIso: start.toISOString(), endIso: end.toISOString() };
  }, [range]);

  const { data: payments, isLoading, error, refetch: refetchPayments } = useQuery<Payment[]>({
    // startIso/endIso are part of the key because the range is now applied
    // server-side — without them, switching Month/Year would not refetch.
    queryKey: ['payments-list', parentData?.id, startIso, endIso],
    queryFn: async () => {
      try {
        let query = supabase
          .from('payments')
          .select(`
            id,
            amount,
            payment_method,
            payment_date,
            status,
            invoice_id,
            paid_by,
            created_at,
            invoice:invoices!inner (
              id,
              total_amount,
              amount_paid,
              due_date,
              period_start,
              period_end,
              status,
              enrollment:enrollments!inner (
                id,
                metadata,
                student:students (
                  id,
                  first_name,
                  last_name
                ),
                program:programs (
                  name,
                  price,
                  billing_type,
                  brand_key
                )
              )
            ),
            bank_account_setup_intents (
              bank_name,
              last4
            ),
            refunds (
              id,
              amount,
              status,
              refund_date
            )
          `)
          // Bound the fetch to the range actually rendered. This query used to
          // pull the parent's entire payment history with four levels of embeds
          // and filter client-side; for a dual-role admin/parent the admin RLS
          // branch matches every row in the table, which is how this ended up
          // hitting the 8s statement timeout.
          .gte('created_at', startIso)
          .lt('created_at', endIso)
          .order('created_at', { ascending: false });

        // Filter by student IDs associated with the parent
        if (parentData?.id) {
          const { data: studentParents } = await supabase
            .from('student_parents')
            .select('student_id')
            .eq('parent_id', parentData.id)
            .eq('can_pay', true);

          const parentStudentIds = (studentParents as StudentParent[] | null)?.map(sp => sp.student_id) || [];
          query = query.in('invoice.enrollment.student_id', parentStudentIds);
        }

        const { data, error } = await query;
        if (error) throw error;
        return (data as unknown as SupabasePaymentResponse[]).map(payment => ({
          ...payment,
          invoice: {
            ...payment.invoice,
            period_start: payment.invoice.period_start,
            period_end: payment.invoice.period_end,
            enrollment: payment.invoice.enrollment
          }
        })) as Payment[];
      } catch (err) {
        console.error('Error fetching payments:', err);
        throw err;
      }
    },
    // Only load payment history when the History tab has been visited
    enabled: !!parentData?.id && visitedTabs.has('history'),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  // A bank bundle payment still clearing has no payments rows yet; list its shares too.
  const { data: bundleSharesInFlight } = useQuery<Payment[]>({
    queryKey: ['payments-bundles-in-flight', parentData?.id],
    queryFn: fetchBundleSharesInFlight,
    enabled: !!parentData?.id && visitedTabs.has('history'),
    staleTime: 30 * 1000,
  });

  // Invoices logic (moved from Invoices.tsx)
  const { data: invoices, isLoading: isInvoicesLoading, error: invoicesError } = useQuery<Invoice[]>({
    queryKey: ['invoices-list', parentData?.id],
    queryFn: async () => {
      try {
        if (!parentData?.id) throw new Error('No parent');

        const { data: studentParents } = await supabase
          .from('student_parents')
          .select('student_id')
          .eq('parent_id', parentData.id)
          .eq('can_pay', true);

        const studentIds = (studentParents as StudentParent[] | null)?.map(sp => sp.student_id) || [];

        const invQuery = supabase
          .from('invoices')
          .select(`
            id,
            enrollment_id,
            total_amount,
            amount_paid,
            due_date,
            period_start,
            period_end,
            status,
            is_prorated,
            created_at,
            enrollment:enrollments!inner (
              id,
              metadata,
              student:students (
                id,
                first_name,
                last_name
              ),
              program:programs (
                id,
                name,
                type,
                price,
                billing_type,
                brand_key,
                active,
                created_at,
                updated_at
              )
            ),
            payments (
              id,
              amount,
              payment_method,
              payment_date,
              status,
              bank_account_setup_intents (
                bank_name,
                last4
              )
            )
          `)
          .in('enrollment.student_id', studentIds)
          .order('created_at', { ascending: false });

        // Client-side filtering will be applied below

        const { data, error } = await invQuery;

        if (error) throw error;

        // Flatten nested arrays to objects and cast to Invoice[]
        return ((data ?? []) as unknown as RawInvoice[]).map((invoice: RawInvoice) => ({
          ...invoice,
          enrollment: invoice.enrollment && Array.isArray(invoice.enrollment)
            ? {
                ...invoice.enrollment[0],
                student: invoice.enrollment[0]?.student?.[0] ?? null,
                program: invoice.enrollment[0]?.program?.[0] ?? null,
              }
            : invoice.enrollment,
        })) as unknown as Invoice[];
      } catch (err) {
        console.error('Error fetching invoices:', err);
        throw err;
      }
    },
    // Only load when invoices tab has been visited (default tab, so loads on first render)
    enabled: !!parentData?.id && visitedTabs.has('invoices'),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  // Memoized so the array reference is stable — prevents studentIdSet and
  // both filtered-list memos from rebuilding on every render when null (all selected).
  const effectiveStudentIds = useMemo(
    () => selectedStudentIds ?? (studentOptions ?? []).map(s => s.id),
    [selectedStudentIds, studentOptions]
  );

  const studentIdSet = useMemo(() => new Set(effectiveStudentIds), [effectiveStudentIds]);

  const cartHeldInvoiceIds = useCartHeldInvoiceIds({ enabled: !!parentData?.id && visitedTabs.has('invoices') });
  const liveZelleCarts = useLiveZelleCarts(!!parentData?.id && visitedTabs.has('invoices'));

  const filteredInvoices = useMemo(() => {
    // If students haven't loaded yet, defer — don't show empty state
    if (effectiveStudentIds.length === 0) return [];
    
    const list = (invoices ?? []) as Array<Invoice & { created_at?: string }>; // invoices include created_at
    const withinRange = (iso?: string) => {
      if (!startIso && !endIso) return true;
      if (!iso) return false;
      const ts = new Date(iso).getTime();
      const after = startIso ? ts >= new Date(startIso).getTime() : true;
      const before = endIso ? ts < new Date(endIso).getTime() : true;
      return after && before;
    };
    const filtered = list.filter(inv => withinRange(inv.created_at) && inv.enrollment?.student && studentIdSet.has((inv.enrollment.student as Student).id));
    return filtered.sort((a, b) => new Date((b.created_at ?? '')).getTime() - new Date((a.created_at ?? '')).getTime());
  }, [invoices, startIso, endIso, studentIdSet, effectiveStudentIds.length]);

  const filteredPayments = useMemo(() => {
    if (effectiveStudentIds.length === 0) return [];
    
    const list = [...(payments ?? []), ...(bundleSharesInFlight ?? [])];
    const withinRange = (iso?: string) => {
      if (!startIso && !endIso) return true;
      if (!iso) return false;
      const ts = new Date(iso).getTime();
      const after = startIso ? ts >= new Date(startIso).getTime() : true;
      const before = endIso ? ts < new Date(endIso).getTime() : true;
      return after && before;
    };
    const filtered = list.filter(p => withinRange(p.created_at ?? p.payment_date) && p.invoice?.enrollment?.student && studentIdSet.has((p.invoice.enrollment.student as Student).id));
    return filtered.sort((a, b) => new Date(((b.created_at ?? b.payment_date) as string)).getTime() - new Date(((a.created_at ?? a.payment_date) as string)).getTime());
  }, [payments, bundleSharesInFlight, startIso, endIso, studentIdSet, effectiveStudentIds.length]);

  // An invoice is processing while a payment on it is in flight -- its own row, or an ACH
  // bundle payment that has not settled (which writes no row until it does).
  const isInvoiceProcessing = (invoice: Invoice) => isPaymentInFlight(invoice, cartHeldInvoiceIds);
  // What the cart page would hold: everything the family can pay now. Not narrowed by the
  // date or student filters -- those choose what the list below shows, not what is owed.
  const dueNowInvoices = (invoices ?? []).filter(inv => isDueNow(inv, cartHeldInvoiceIds, liveZelleCarts)).sort(compareDueNow);
  const dueNowTotalCents = dueNowInvoices.reduce((sum, inv) => sum + balanceDueCents(inv), 0);
  const showDueNowBanner = !isInvoicesLoading && dueNowInvoices.length > 0;
  const dueNowMetadata = () => ({
    invoice_count: dueNowInvoices.length,
    due_cents: dueNowTotalCents,
    invoice_ids: dueNowInvoices.map(inv => inv.id),
  });

  // Once per visit: did a family with the cart see the option at all? The start of the
  // banner → Checkout → cart_checkout_start → cart_payment_success funnel.
  const dueNowBannerLogged = useRef(false);
  useEffect(() => {
    if (!showDueNowBanner || activeTab !== 'invoices' || !user || dueNowBannerLogged.current) return;
    dueNowBannerLogged.current = true;
    trackEvent({ eventType: 'cart_banner_view', user, sessionId: getSessionId(), metadata: dueNowMetadata() });
    // dueNowMetadata reads the same render's values; logging once is the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDueNowBanner, activeTab, user]);

  if (isLoading) {
    return (
      <div className="p-3 sm:p-4" data-testid="loading-skeleton">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white p-6 rounded-lg shadow space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 sm:p-4">
        <div role="alert" className="bg-red-50 border border-red-200 rounded p-4">
          <p className="text-sm sm:text-base text-red-700 font-medium">Couldn&apos;t load your payment history</p>
          <p className="mt-1 text-xs sm:text-sm text-red-600">{getErrorMessage(error)}</p>
          <button
            type="button"
            onClick={() => refetchPayments()}
            className="mt-3 text-sm font-medium px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white overflow-hidden">
            <button
              type="button"
              className={`px-3 py-1.5 text-sm focus:outline-none ${range === 'month' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'} rounded-l-lg`}
              onClick={() => setRange('month')}
            >
              Month
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 text-sm focus:outline-none ${range === 'year' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
              onClick={() => setRange('year')}
            >
              Year
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 text-sm focus:outline-none ${range === 'all' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'} rounded-r-lg`}
              onClick={() => setRange('all')}
            >
              All
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex flex-wrap gap-2">
            {!parentData || isStudentsLoading ? (
              <div className="text-xs sm:text-sm text-gray-500">
                Loading students...
              </div>
            ) : studentOptions && studentOptions.length > 0 ? (
              studentOptions.map((s) => {
                const isOn = selectedStudentIds === null || selectedStudentIds.includes(s.id)
                const solid = s.active
                const classes = isOn
                  ? (solid ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700')
                  : (solid ? 'bg-gray-200 text-gray-700' : 'bg-gray-100 text-gray-500')
                return (
                  <button
                    key={s.id}
                    type="button"
                    className={`px-3 py-1.5 text-sm rounded-full focus:outline-none transition-colors ${classes}`}
                    onClick={() => toggleStudent(s.id)}
                    title={s.active ? 'Active' : 'Inactive'}
                  >
                    {s.first_name}
                  </button>
                )
              })
            ) : (
              <div className="text-xs sm:text-sm text-gray-500">
                No students found
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Subtabs */}
      <div className="flex space-x-4 mb-8 border-b border-gray-200">
        <button
          className={`px-4 py-2 -mb-px text-base font-medium border-b-2 transition-colors duration-150 focus:outline-none ${activeTab === 'invoices' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-blue-600'}`}
          aria-current={activeTab === 'invoices' ? 'page' : undefined}
          onClick={() => setActiveTab('invoices')}
        >
          {t('tab.invoices')}
        </button>
        <button
          className={`px-4 py-2 -mb-px text-base font-medium border-b-2 transition-colors duration-150 focus:outline-none ${activeTab === 'history' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-blue-600'}`}
          aria-current={activeTab === 'history' ? 'page' : undefined}
          onClick={() => setActiveTab('history')}
        >
          {t('label.history')}
        </button>
        <Link
          to="/wallet"
          className={`px-4 py-2 -mb-px text-base font-medium border-b-2 transition-colors duration-150 focus:outline-none ${location.pathname === '/wallet' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-blue-600'}`}
          data-testid="bhcs-cash-tab"
        >
          {t('tab.cash')}
          {cashTotalCents !== undefined && (
            <span className="ml-1.5 text-sm font-semibold">
              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cashTotalCents / 100)}
            </span>
          )}
        </Link>
      </div>
      {/* Tab Content */}
      {activeTab === 'invoices' && (
        <div className="py-4">
          {/* One link per language: the question is prefilled as written, and the assistant
              answers in Chinese whenever a question contains any, so a combined label would
              answer English speakers in Chinese. */}
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 mb-4 text-xs font-medium text-blue-600">
            <Sparkles className="h-3.5 w-3.5" />
            {[explainInvoice.zh, explainInvoice.en].filter((q): q is string => !!q).map((q, i) => (
              <Fragment key={q}>
                {i > 0 && <span className="text-gray-300" aria-hidden="true">·</span>}
                <Link
                  to={`/assistant?source=invoices&q=${encodeURIComponent(q)}`}
                  className="hover:text-blue-700 hover:underline"
                >
                  {q}
                </Link>
              </Fragment>
            ))}
          </div>
          {showDueNowBanner && (
            <div
              className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 sm:px-6"
              data-testid="due-now-banner"
            >
              <div className="text-sm text-blue-900">
                <span className="font-semibold">{t('label.dueNow')}:</span>{' '}
                {dueNowInvoices.length} invoice{dueNowInvoices.length === 1 ? '' : 's'} · ${(dueNowTotalCents / 100).toFixed(2)}
                {dueNowInvoices.length > 1 && (
                  <span className="block text-xs text-blue-700 mt-0.5">Pay them all in one payment, or choose which.</span>
                )}
              </div>
              <button
                type="button"
                className="bg-blue-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                onClick={() => {
                  // One invoice is never a bundle: it goes to its own checkout.
                  const single = dueNowInvoices.length === 1;
                  trackEvent({
                    eventType: 'cart_checkout_click',
                    user,
                    sessionId: getSessionId(),
                    metadata: { ...dueNowMetadata(), ...(single ? { single: true } : {}) },
                  });
                  navigate(single ? checkoutPath(dueNowInvoices[0].id, 'due_now') : '/cart');
                }}
                data-testid="due-now-checkout"
              >
                {dueNowInvoices.length === 1 ? t('action.payNow') : `${t('action.checkout')} (${dueNowInvoices.length})`}
              </button>
            </div>
          )}
          {isInvoicesLoading ? (
            <div data-testid="loading-skeleton" className="animate-pulse space-y-4">
              <div className="h-8 bg-gray-200 rounded w-1/4"></div>
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="bg-white p-6 rounded-lg shadow space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </div>
                ))}
              </div>
            </div>
          ) : invoicesError ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
              <strong className="font-bold">Error: </strong>
              <span className="text-xs sm:text-sm block sm:inline">Unable to load invoices. Please try again later.</span>
            </div>
          ) : (!filteredInvoices || filteredInvoices.length === 0) ? (
            <div className="bg-gray-50 border border-gray-200 text-gray-700 px-4 py-3 rounded relative" role="alert">
              <span className="text-xs sm:text-sm block sm:inline">No invoices found.</span>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredInvoices.map((invoice: Invoice) => {
                const isPaymentProcessing = isInvoiceProcessing(invoice);

                return (
                    <div
                      key={invoice.id}
                      className="bg-white rounded-2xl shadow p-4 sm:p-6 hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => navigate(`/invoices/${invoice.id}`)}
                    >
                      {/* Status at top, consistent with details page. The list spans every
                          brand the family buys from, so each invoice names its own. */}
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isPaymentProcessing ? 'bg-blue-100 text-blue-800' : getInvoiceStatusColor(invoice.status)}`}>
                          {statusLabel(isPaymentProcessing ? 'payment_processing' : invoice.status)}
                        </span>
                        <BrandBadge brand={invoice.enrollment?.program?.brand_key} />
                        {liveZelleCarts.has(invoice.id) && (
                          <Link
                            to={`/cart/${liveZelleCarts.get(invoice.id)!.cartId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-900 border border-yellow-200 hover:bg-yellow-100"
                            data-testid={`zelle-code-${invoice.id}`}
                          >
                            Zelle code {liveZelleCarts.get(invoice.id)!.memoCode}
                          </Link>
                        )}
                      </div>
                      <div className="flex items-start justify-between gap-4">
                      {/* Left column: student, program, dates */}
                      <div className="min-w-0">
                        <div className="text-xs text-gray-500 mb-1">Invoice #{invoice.id}</div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                            {invoice.enrollment && invoice.enrollment.student
                              ? `${invoice.enrollment.student.first_name} ${invoice.enrollment.student.last_name}`
                              : 'Student'}
                          </h2>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-700">
                          {(() => {
                            const name = invoice.enrollment?.program?.name || 'Program';
                            const lvl = (invoice.enrollment as { metadata?: { level?: string } } | undefined)?.metadata?.level;
                            return typeof lvl === 'string' && lvl.trim() ? `${name} (${lvl})` : name;
                          })()}
                        </p>
                        {/* Proration hint when applicable (moved from amount summary) */}
                        {(() => {
                          if (!invoice.is_prorated) return false;
                          return (
                            <p className="text-xs text-yellow-700 mt-1 font-medium">
                              ℹ️ Prorated billing - view invoice details for full breakdown
                            </p>
                          );
                        })()}
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          <span className="inline-flex items-center text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                            {formatDateDisplay(invoice.period_start)} – {formatDateDisplay(invoice.period_end)}
                          </span>
                          <span className="inline-flex items-center text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                            Due {formatDateDisplay(invoice.due_date)}
                          </span>
                        </div>
                      </div>

                      {/* Right column: amounts and action */}
                      <div className="flex flex-col items-end gap-2 md:text-right shrink-0">
                        <div>
                          <div className="text-xs text-gray-600">Balance Due</div>
                          <div className="text-xl sm:text-2xl font-bold text-gray-900">
                            {typeof invoice.total_amount === 'number' && typeof invoice.amount_paid === 'number'
                              ? `$${(invoice.total_amount - invoice.amount_paid).toFixed(2)}`
                              : 'N/A'}
                          </div>
                      {typeof invoice.total_amount === 'number' && (
                        <div className="text-xs text-gray-600 mt-1">
                          Total ${invoice.total_amount.toFixed(2)}
                        </div>
                      )}
                        </div>
                        {invoice.status !== 'paid' && invoice.status.toLowerCase() !== 'voided' && !isPaymentProcessing && (
                          <button
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(checkoutPath(invoice.id, 'invoice_list'));
                            }}
                          >
                            {t('action.payNow')}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Payment History - no divider */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xs sm:text-sm font-medium text-gray-900">{t('header.paymentHistory')}</h3>
                        {/* Primary payment CTA is the "Pay Now" button above */}
                      </div>
                      {invoice.payments && invoice.payments.length > 0 ? (
                        <div className="divide-y divide-gray-200">
                          {invoice.payments.map((payment: Payment) => (
                            <div key={payment.id} className="py-3 flex items-start justify-between">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 text-sm text-gray-700 flex-wrap">
                                  <span className="font-medium text-gray-800">
                                    {formatPaymentMethod(payment.payment_method, payment.bank_account_setup_intents)}
                                  </span>
                                  {(payment.payment_method === 'bhcs_cash' || payment.payment_method === 'wallet') && (
                                    <Link to="/wallet" className="text-blue-600 hover:underline text-xs" onClick={e => e.stopPropagation()}>View wallet ↗</Link>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500">{formatDateDisplay(payment.payment_date)}</div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-sm font-medium text-gray-900">${payment.amount.toFixed(2)}</div>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(payment.status)}`}>{payment.status}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs sm:text-sm text-gray-500 text-center py-4">No payments recorded for this invoice</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {activeTab === 'history' && (
        <div className="space-y-6">
          {(!filteredPayments || filteredPayments.length === 0) ? (
            <div className="text-center py-12">
              <p className="text-xs sm:text-base text-gray-500">No payment history found</p>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredPayments.map((payment: Payment) => (
                <div key={payment.id} className="bg-white rounded-2xl shadow p-4 sm:p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: who and what */}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                          {payment.status.toUpperCase()}
                        </span>
                        {parentData?.id && payment.paid_by && parentData.id === payment.paid_by && (
                          <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs font-medium">Paid by me</span>
                        )}
                        <BrandBadge brand={payment.invoice.enrollment.program?.brand_key} />
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600">Student: {payment.invoice.enrollment.student.first_name} {payment.invoice.enrollment.student.last_name}</p>
                      <p className="text-xs sm:text-sm text-gray-700">
                        {(() => {
                          const name = payment.invoice.enrollment.program.name;
                          const lvl = payment.invoice.enrollment.metadata?.level;
                          return typeof lvl === 'string' && lvl.trim() ? `${name} (${lvl})` : name;
                        })()}
                      </p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="text-xs text-gray-600">
                          {formatPaymentMethod(payment.payment_method, payment.bank_account_setup_intents)}
                        </span>
                        {(payment.payment_method === 'bhcs_cash' || payment.payment_method === 'wallet') && (
                          <Link to="/wallet" className="text-blue-600 hover:underline text-xs">View wallet ↗</Link>
                        )}
                      </div>
                    </div>

                    {/* Right: amount and date */}
                    <div className="text-right shrink-0">
                      <div className="text-xs text-gray-600">Amount</div>
                      <div className="text-xl sm:text-2xl font-bold text-gray-900">${payment.amount.toFixed(2)}</div>
                      <div className="text-xs text-gray-600 mt-1">{formatDateDisplay(payment.payment_date)}</div>
                    </div>
                  </div>

                  {/* Divider + actions */}
                  <div className="border-t mt-4 pt-3 flex items-center justify-between">
                    <Link to={`/invoices/${payment.invoice.id}`} className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      {t('action.viewInvoice')}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}